// Zone DRESSING, and the light kinds the shop is lit with.
//
// Seven zones, each with the wall treatment, floor tint, fixture style, palette
// and lighting its part of the room is dressed in. This is the older of the two
// layout tables and it is still live: the texture layer keys its zone dressing
// off it.
//
// It is NOT where the rooms come from. The five alcoves the player can walk
// into (entry hall, reggae room, listening room, jazz room, machine room) live
// in zones.js. The two alcoves named here, soul and dub, are superseded. Having
// layout facts in two tables is exactly the bug class in CLAUDE.md section 4 —
// treat zones.js as authoritative and do not add anything new here.
//
// Recovered verbatim from the production bundle; the "note" fields are original.

const LIGHT_KINDS = {
        TUNGSTEN_WARM: {
            color: 16751173,
            kelvin: 2400,
            note: "old enamel pendant, deep amber"
        },
        TUNGSTEN: {
            color: 16757094,
            kelvin: 2700,
            note: "standard shop pendant"
        },
        FLUORESCENT: {
            color: 16773592,
            kelvin: 3500,
            note: "the big diffuser panel, near-neutral"
        },
        COOL_STRIP: {
            color: 14216447,
            kelvin: 4200,
            note: "under-shelf LED, the only cool light in the shop"
        },
        NEON_RED: {
            color: 16722492,
            kelvin: null,
            note: "sign glow, tiny throw, huge character"
        },
        NEON_CYAN: {
            color: 2807039,
            kelvin: null,
            note: "sign glow"
        },
        BRASS_PICTURE: {
            color: 16763274,
            kelvin: 2500,
            note: "picture light over faced-out sleeves"
        }
    },
    ZONES = [{
        id: "rock",
        name: "ROCK & PUNK",
        sign: {
            text: "ROCK / PUNK",
            style: "stencil",
            extra: ["NEW ARRIVALS", "USED LP £5"]
        },
        bounds: {
            z0: 4.6,
            z1: 8.2
        },
        blurb: "Right inside the door and the loudest thing in the room.",
        wall: {
            kind: "flyer-collage",
            density: 1,
            base: 3813672,
            overlapping: !0
        },
        floor: {
            kind: "plank",
            tint: 9407104,
            wear: 1
        },
        fixture: {
            kind: "crate",
            timber: "blackened",
            tint: 4866618,
            rimWear: 1
        },
        light: {
            main: LIGHT_KINDS.TUNGSTEN,
            accent: {
                ...LIGHT_KINDS.NEON_RED,
                kind: "sign",
                intensity: 2.4,
                distance: 2.2
            },
            exposureBias: 0
        },
        palette: ["#0d0d0f", "#e02a24", "#f2ece0", "#8a1a14", "#c9c4b8"],
        saturation: .62,
        props: ["flyer-stack", "staple-gun", "tape-deck", "stool"]
    }, {
        id: "hiphop",
        name: "HIP-HOP",
        sign: {
            text: 'HIP-HOP / 12"',
            style: "marker",
            extra: ["BREAKS", "WHITE LABELS"]
        },
        bounds: {
            z0: 1.2,
            z1: 4.6
        },
        blurb: 'Deeper bins for 12" singles, and a wall of cassettes nobody has priced.',
        wall: {
            kind: "brick-tagged",
            density: .7,
            base: 3025446,
            tag: 14263594
        },
        floor: {
            kind: "plank",
            tint: 9078139,
            wear: 1
        },
        fixture: {
            kind: "crate-12",
            timber: "raw",
            tint: 10259054,
            depth: .42
        },
        light: {
            main: LIGHT_KINDS.FLUORESCENT,
            accent: {
                ...LIGHT_KINDS.TUNGSTEN_WARM,
                kind: "shelf",
                intensity: 1.6,
                distance: 2
            },
            exposureBias: .05
        },
        palette: ["#141210", "#d9a52a", "#8c8c8c", "#f0e9dd", "#5a4a2e"],
        saturation: .5,
        props: ["cassette-wall", "boombox", "crate-stack", "turntable"]
    }, {
        id: "funk",
        name: "FUNK & DISCO",
        sign: {
            text: "FUNK / DISCO",
            style: "bubble",
            extra: ['7" SOUL 50p']
        },
        bounds: {
            z0: -2.2,
            z1: 1.2
        },
        blurb: "The only section with anything shiny in it.",
        wall: {
            kind: "foil-strip",
            density: .6,
            base: 3810368,
            sheen: .35
        },
        floor: {
            kind: "plank",
            tint: 9405819,
            wear: .9
        },
        fixture: {
            kind: "crate",
            timber: "raw",
            tint: 10521190,
            trim: "chrome"
        },
        light: {
            main: LIGHT_KINDS.TUNGSTEN,
            accent: {
                color: 16767472,
                kind: "mirrorball",
                intensity: 1.4,
                distance: 3.4,
                speckles: 9
            },
            exposureBias: .05
        },
        palette: ["#ff7a1a", "#7b2ff7", "#c0c0c8", "#ffd23f", "#241432"],
        saturation: .85,
        props: ["mirror-panel", "seven-inch-rack", "dancefloor-poster"]
    }, {
        id: "electronic",
        name: "ELECTRONIC",
        sign: {
            text: "ELECTRONIC / TECHNO",
            style: "vinyl-cut",
            extra: ["IMPORTS", "PROMO"]
        },
        bounds: {
            z0: -5.4,
            z1: -2.2
        },
        blurb: "Plain sleeves, no artwork, the coldest light in the building.",
        wall: {
            kind: "painted-flat",
            density: .25,
            base: 1974566
        },
        floor: {
            kind: "concrete",
            tint: 7237234,
            wear: .5
        },
        fixture: {
            kind: "crate",
            timber: "painted-black",
            tint: 2763310
        },
        light: {
            main: LIGHT_KINDS.COOL_STRIP,
            accent: {
                ...LIGHT_KINDS.NEON_CYAN,
                kind: "sign",
                intensity: 2,
                distance: 2
            },
            exposureBias: -.05
        },
        palette: ["#e8e8e6", "#101214", "#2ad4ff", "#9a9a96", "#3a3a3e"],
        saturation: .22,
        props: ["white-label-bin", "listening-post", "flightcase"]
    }, {
        id: "jazz",
        name: "JAZZ",
        sign: {
            text: "JAZZ",
            style: "letterpress",
            extra: ["BLUE NOTE", "A–M", "N–Z"]
        },
        bounds: {
            z0: -8.2,
            z1: -5.4
        },
        blurb: "The quiet end. One lamp, framed prints, a chair nobody sits in.",
        wall: {
            kind: "framed-prints",
            density: .35,
            base: 2304563
        },
        floor: {
            kind: "plank",
            tint: 8288882,
            wear: .6
        },
        fixture: {
            kind: "shelf-deep",
            timber: "dark-stained",
            tint: 4865070
        },
        light: {
            main: LIGHT_KINDS.TUNGSTEN_WARM,
            accent: {
                ...LIGHT_KINDS.BRASS_PICTURE,
                kind: "picture",
                intensity: 2.2,
                distance: 1.8
            },
            exposureBias: -.12
        },
        palette: ["#1b2b46", "#e0b23c", "#f0ebdd", "#8a6a2a", "#2e4a6a"],
        saturation: .45,
        props: ["armchair", "framed-print", "ashtray", "floor-lamp"]
    }, {
        id: "soul",
        name: "SOUL & R&B",
        sign: {
            text: "SOUL / R&B",
            style: "painted-board",
            extra: ["NORTHERN", "BALLADS"]
        },
        alcove: {
            side: -1,
            z: .4,
            w: 2.6,
            d: 2.8,
            openingW: 1.5
        },
        blurb: "A back room done out like a front parlour. Carpet, not boards.",
        wall: {
            kind: "flock-wallpaper",
            density: .5,
            base: 5906472,
            pattern: "damask"
        },
        floor: {
            kind: "carpet",
            tint: 4857896,
            wear: .7
        },
        fixture: {
            kind: "browser-low",
            timber: "dark-stained",
            tint: 5455916,
            trim: "brass"
        },
        light: {
            main: LIGHT_KINDS.TUNGSTEN_WARM,
            accent: {
                ...LIGHT_KINDS.BRASS_PICTURE,
                kind: "picture",
                intensity: 2.6,
                distance: 2
            },
            exposureBias: .02
        },
        palette: ["#7a1f2e", "#e8c27a", "#f2e6d4", "#3a1018", "#c98a4a"],
        saturation: .6,
        props: ["armchair", "lamp-table", "record-player", "framed-portrait"]
    }, {
        id: "dub",
        name: "REGGAE & DUB",
        sign: {
            text: "REGGAE / DUB",
            style: "hand-painted",
            extra: ['7" PRESSINGS', "DANCEHALL"]
        },
        alcove: {
            side: 1,
            z: -3.4,
            w: 2.4,
            d: 2.6,
            openingW: 1.4
        },
        blurb: "Sun-bleached sleeves, a speaker stack, and the warmest bulb in the shop.",
        wall: {
            kind: "sunfaded-poster",
            density: .8,
            base: 3029540,
            fade: .4
        },
        floor: {
            kind: "plank",
            tint: 9077880,
            wear: 1
        },
        fixture: {
            kind: "crate",
            timber: "raw",
            tint: 10259054
        },
        light: {
            main: LIGHT_KINDS.TUNGSTEN_WARM,
            accent: {
                color: 16763955,
                kind: "bulb-bare",
                intensity: 1.8,
                distance: 2.4
            },
            exposureBias: 0
        },
        palette: ["#1e6b3a", "#f0c419", "#c1272d", "#f2ead8", "#2a2a1e"],
        saturation: .78,
        props: ["speaker-stack", "seven-inch-rack", "soundsystem-flyer"]
    }];

const ZONE_BY_ID = Object.fromEntries(ZONES.map((z) => [z.id, z]));
const BANDS = ZONES.filter((z) => z.bounds);
const ALCOVES = ZONES.filter((z) => z.alcove);

export { LIGHT_KINDS, ZONES, ZONE_BY_ID, BANDS, ALCOVES };
